import type { DocPageContent } from '../docs-content'

const page = {
  order: 15.75,
  slug: 'motion',
  title: '使用运动工作区',
  description:
    '在运动工作区中构建、安排时序、制作动画、建立父子关系并复用分层 2D 合成体。',
  category: '创意工具',
  related: ['motion-library', 'keyframes', 'shapes-masks', 'concepts'],
  sections: [
    {
      title: '打开或创建运动合成体',
      blocks: [
        {
          kind: 'paragraph',
          text: '运动将媒体库、合成预览、图层时间轴、属性、关键帧、曲线图与运动库整合在同一个工作区中。',
        },
        {
          kind: 'steps',
          items: [
            '在工作区切换器中选择**运动**，或按 `Alt+3`。',
            '从合成菜单中选择一个已有的合成体，或选择**新建合成**。',
            '要将序列片段提升为运动片段，请在编辑中选中它们，打开**动画**，并选择**创建运动片段**。',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '创建运动片段会将所选片段及其动画保留在新的合成体内部。新的包装在编辑中仍是一个普通片段。',
        },
      ],
    },
    {
      title: '向合成体添加图层',
      blocks: [
        {
          kind: 'list',
          items: [
            '从媒体库拖入视频、音频、图像、文本、形状、Lottie 文件，或另一个合成体。',
            '选择**添加项目**以创建**文本**、**纯色**、**渐变**、**形状**或**空对象**图层。',
            '纯色与渐变会创建全帧形状图层，你可以调整大小、设置样式、遮罩并制作动画。',
            '精剪会阻止合成体包含自身，包括间接的循环嵌套。',
          ],
        },
      ],
    },
    {
      title: '设置时长与活动范围',
      blocks: [
        {
          kind: 'paragraph',
          text: '创作时长定义了合成体。标尺上的入点与出点手柄定义了该时长内的活动渲染范围。',
        },
        {
          kind: 'steps',
          items: [
            '拖动运动标尺上方的**入点**与**出点**手柄以标记活动范围。',
            '选择**将合成裁剪至活动区域**，将创作好的合成体缩短到该范围。',
            '选择**缩放适配**以框定创作好的合成体，而不改变其时长。',
            '在属性中编辑**合成时长**，并选择其单位在帧与秒之间切换。',
            '读取时长控件下方对应的**时码**值。',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '修剪会保留入点与出点控件可用，并适配新的范围。延伸到创作时长之外的图层仍会在变暗的超出区域中可见。',
        },
      ],
    },
    {
      title: '排列与安排图层时序',
      blocks: [
        {
          kind: 'list',
          items: [
            '拖动图层跨度以在时间上移动它。选中的图层会在一步撤销中一起移动。',
            '编辑每个图层的**入点**与**出点**字段，或拖动其跨度边缘进行修剪。',
            '使用图层控件更改可见性、锁定、独奏、混合模式与堆叠顺序。',
            '拖动三点手柄以重新排序图层或组。子图层仍保留在其当前组内。',
            '选中两个或多个图层并选择**图层编组**。组的可见性、锁定、独奏、时序与重新排序都会作用于其子项。',
            '右键点击图层或组可重命名、编组、解组、复制、拷贝、粘贴或删除它。',
          ],
        },
      ],
    },
    {
      title: '为属性与路径顶点制作动画',
      blocks: [
        {
          kind: 'list',
          items: [
            '展开图层以显示嵌套的属性组、数值控件、自动关键帧、导航与关键帧菱形标记。',
            '当你只需要包含关键帧的行时，将属性筛选器设为**已动画属性**。',
            '选中属性菱形标记可在共享播放头处添加或移除关键帧。',
            '选中两个相邻关键帧以编辑它们之间的分段缓动。',
            '选中属性曲线图标，可将其轨迹表轨道替换为内联数值曲线图。',
            '打开位置组菜单并选择**分离位置维度**，以独立编辑 X 和 Y。',
          ],
        },
        {
          kind: 'paragraph',
          text: '路径形状会在运动中暴露顶点轨道。在预览中选择路径点，然后在属性行上方于**所选顶点**与**所有顶点**之间切换。',
        },
        {
          kind: 'note',
          tone: 'warning',
          text: '一旦路径几何（Path Geometry）有了关键帧，你就无法添加、移除、重新排序或更改第一个路径顶点。在更改路径拓扑前，请先移除这些关键帧。',
        },
      ],
    },
    {
      title: '用空对象建立图层父子关系',
      blocks: [
        {
          kind: 'steps',
          items: [
            '选择**添加项目 > 空对象**。空对象控制变换但不参与渲染。',
            '从**父级**菜单中选择父级，或将父级拾取鞭（pick whip）拖到另一个图层上。',
            '正常释放可保留子级的画布姿态。',
            '释放时按住 **Shift** 可将子级位置吸附到父级。',
            '释放时按住 **Alt/Option** 可使用子级创作好的本地姿态。',
            'Ctrl 点击或 Command 点击子级拾取鞭，可在保留其当前姿态的同时解除父子关系。',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '拖拽时有效的父级目标会高亮。运动会拒绝自我父级、后代目标，以及会创建循环的链接。',
        },
      ],
    },
    {
      title: '链接属性或编写表达式',
      blocks: [
        {
          kind: 'paragraph',
          text: '父子关系会创建图层层级。而**属性链接**会让一个属性跟随另一个，且不改变该层级。',
        },
        {
          kind: 'list',
          items: [
            '将属性拾取鞭拖到运动中、轨迹表或曲线图里的一个兼容行。',
            '将标量值链接到标量值。位置、缩放与锚点作为完整的二维值进行链接。',
            '选择大括号按钮可添加一个沙箱化的表达式，且不会删除该属性的关键帧。',
            '使用表达式拾取鞭，在文本光标处插入 `prop("layer-id", "property")` 引用。',
            '可使用 `value`、`preValue`、`frame`、`time`、标量或 `[x, y]` 值、算术运算，以及受支持的数学函数。',
          ],
        },
        {
          kind: 'note',
          tone: 'info',
          text: '精剪先求值关键帧，然后是属性链接，最后是表达式。预览与导出使用相同的顺序。',
        },
      ],
    },
    {
      title: '为复用的合成体暴露控件',
      blocks: [
        {
          kind: 'steps',
          items: [
            '在源合成体中打开**已发布控件**。',
            '将编辑者在放置实例上应修改的文本或颜色发布出去。',
            '将合成体放置到一个普通序列中，并选中该实例。',
            '在属性中编辑其已发布的值，而无需改动源合成体。',
            '重置单个覆盖项，或使用**重置所有覆盖**恢复源值。',
          ],
        },
      ],
    },
    {
      title: '在编辑中使用合成体',
      blocks: [
        {
          kind: 'paragraph',
          text: '运动合成体仍然是一种可复用的合成体资源。将其放入序列后，即可像对待任何其他片段一样移动、修剪、制作动画或施加特效于该实例。',
        },
        {
          kind: 'note',
          tone: 'info',
          text: '内部图层动画与整片段动画保持独立。一个标题可以在内部自行动画，而它在编辑中的放置实例同时移动或淡入淡出。',
        },
      ],
    },
  ],
} satisfies DocPageContent

export default page
